<script setup lang="ts">
    import { computed, onMounted, ref, useTemplateRef } from 'vue';
    import { useElementSize } from '@vueuse/core';
    import CodeEdit from '@/shared/codeedit/CodeEdit.vue';
    import CodeRefSelector from '@/shared/components/CodeRefSelector.vue';
    import GovernanceInformation from '@/shared/components/GovernanceInformation.vue';
    import BoolElement from '@/shared/components/BoolElement.vue';
    import {
        fetchCodeEditContext,
        getLibrary,
        searchCodes,
        validateNewCode,
        type CodeEditContextRefCode,
        type CodeSearchHit,
        type CodeSearchResponse
    } from '@/shared/helpers/api';
    import { encodeIdBase64 } from '@/shared/helpers/utils';
    import { getCodeLinkByB64Id } from '@/shared/helpers/routing';
    import { http } from '@/shared/httpWrapper';
    import useBuefy from '@/shared/composables/useBuefy';
    import useShow from '@/shared/composables/useShow';
    import {
        buildSearchRequest,
        getFindOutcome,
        getIdentityFields,
        isSearchEmpty,
        toTemplateValues,
        type IdentityField,
        type SearchTerms
    } from './codeFindOrEdit';
    import type { CodeTemplateValues } from './codeTemplate';

    const props = defineProps<{
        library: string;
        codeSetName: string;
        scopes?: string[];
    }>();

    const emit = defineEmits<{
        refresh: [];
        cancel: [];
    }>();

    const buefy = useBuefy();
    const { showError } = useShow();

    const loading = ref(true);
    const loadError = ref<string | null>(null);
    const identityFields = ref<IdentityField[]>([]);
    const referableCodes = ref<Record<string, CodeEditContextRefCode[]>>({});
    const searchTerms = ref<SearchTerms>({});
    const emptySearch = ref(false);

    const searching = ref(false);
    const response = ref<CodeSearchResponse | null>(null);
    const searchedTerms = ref<SearchTerms>({});
    // The reasons saving would reject the new item, from the check Create runs; null until it has run
    const createErrors = ref<string[] | null>(null);
    const checkingCreate = ref(false);

    type Editor = { mode: 'update'; hit: CodeSearchHit } | { mode: 'create'; template: CodeTemplateValues; label: string };
    const editor = ref<Editor | null>(null);
    const editorKey = ref(0);
    const editorLoaded = ref(false);

    const outcome = computed(() => response.value
        ? getFindOutcome(response.value, searchedTerms.value, createErrors.value)
        : null);

    const rows = computed(() => {
        const codes = response.value?.codes ?? [];
        const exact = outcome.value?.exactMatch;
        return exact && !codes.some(c => c.id === exact.id) ? [exact, ...codes] : codes;
    });

    const createLabel = computed(() => {
        const name = (searchedTerms.value.name ?? '').trim();
        return name || response.value?.exactIdentity || '';
    });

    const usesWildcard = computed(() => identityFields.value.some(f => f.field === 'name'));

    // A reference picker opens its list below the field, inside the modal. Keep room for the longest list below the
    // find form, so the list fits without scrolling the modal. Buefy's autocomplete list is at most 200px tall.
    const PICKER_LIST_ROOM = '200px';
    const findForm = useTemplateRef<HTMLFormElement>('findForm');
    const { height: findFormHeight } = useElementSize(findForm);
    const rootStyle = computed(() => identityFields.value.some(f => f.type === 'CodeRef')
        ? { minHeight: `calc(${findFormHeight.value}px + ${PICKER_LIST_ROOM})` }
        : {});

    onMounted(async () => {
        try {
            const library = await getLibrary(http, props.library, {
                includeAccessGroups: false,
                includeAttributeDefintions: true,
                includeAttachment: false,
                includeTags: false,
                includeCodeSets: false
            });

            identityFields.value = getIdentityFields(library);
            searchTerms.value = Object.fromEntries(identityFields.value.map(f => [f.field, '']));

            const identityCodeRefs = identityFields.value.filter(f => f.type === 'CodeRef').map(f => f.field);
            if (identityCodeRefs.length > 0) {
                const otherCodeRefs = (library.attributeDefinitions ?? [])
                    .filter(ad => ad.attributeType === 'CodeRef' && !identityCodeRefs.includes(ad.name))
                    .map(ad => ad.name);
                const context = await fetchCodeEditContext(http, {
                    codeSetName: props.codeSetName,
                    codeRefsNotToLoad: otherCodeRefs,
                    includeFilteredReferences: true
                });
                // Existing items may refer to values outside a field's static filter, so every value can be searched
                // for, and the ones the filter excludes are marked. Whether a value may be used for a new item is
                // checked by the server when the user selects Create.
                const refsByField: typeof referableCodes.value = {};
                for (const field of identityCodeRefs) {
                    const codes = context.data.referableCodes[field] ?? [];
                    refsByField[field] = codes.map(c => ({
                        ...c,
                        isFiltered: false,
                        description: c.isFiltered ? [c.description, '(outside filter)'].filter(Boolean).join(' ') : c.description
                    }));
                }
                referableCodes.value = refsByField;
            }
        } catch (error) {
            loadError.value = 'Could not load the library definition.';
            showError(error);
        } finally {
            loading.value = false;
        }
    });

    function confirmLeavingEditor(onConfirm: () => void) {
        if (!editor.value) {
            onConfirm();
            return;
        }

        buefy.dialog.confirm({
            title: 'Leave the item editor?',
            message: 'Any unsaved changes to the item will be lost.',
            confirmText: 'Leave editor',
            type: 'is-warning',
            hasIcon: true,
            onConfirm
        });
    }

    function onFind() {
        if (isSearchEmpty(searchTerms.value)) {
            emptySearch.value = true;
            return;
        }
        emptySearch.value = false;
        confirmLeavingEditor(() => find({ ...searchTerms.value }));
    }

    async function find(searched: SearchTerms) {
        editor.value = null;
        searching.value = true;
        try {
            response.value = (await searchCodes(http, buildSearchRequest(props.codeSetName, identityFields.value, searched))).data;
            searchedTerms.value = searched;
            createErrors.value = null;
        } catch (error) {
            showError(error);
        } finally {
            searching.value = false;
        }
    }

    function openEditor(next: Editor) {
        editorLoaded.value = false;
        editorKey.value++;
        editor.value = next;
    }

    function editItem(hit: CodeSearchHit) {
        openEditor({ mode: 'update', hit });
    }

    /**
     * Checks with the server whether saving would accept a new item with the searched values, which reads the whole
     * set, so it only runs when the user wants to create. Opens the editor when it would, and lists the reasons when not.
     */
    async function createItem() {
        checkingCreate.value = true;
        try {
            const check = (await validateNewCode(http, buildSearchRequest(props.codeSetName, identityFields.value, searchedTerms.value))).data;
            createErrors.value = check.errors;
            if (check.errors.length === 0) {
                openEditor({ mode: 'create', template: toTemplateValues(identityFields.value, searchedTerms.value), label: createLabel.value });
            }
        } catch (error) {
            showError(error);
        } finally {
            checkingCreate.value = false;
        }
    }

    function closeEditor() {
        editor.value = null;
    }

    async function onSaved() {
        editor.value = null;
        emit('refresh');
        await find(searchedTerms.value);
    }

    function clearField(field: string) {
        searchTerms.value[field] = '';
    }

    /** Closes the component, asking first when the item editor is open. */
    function requestClose() {
        confirmLeavingEditor(() => emit('cancel'));
    }

    defineExpose({ requestClose });
</script>

<template>
    <div
        class="code-find-or-edit"
        :style="rootStyle">
        <b-loading
            :model-value="loading"
            :is-full-page="false" />

        <b-notification
            v-if="loadError"
            type="is-danger"
            :closable="false">
            {{ loadError }}
        </b-notification>

        <form
            v-else-if="!loading"
            ref="findForm"
            @submit.prevent="onFind">
            <p class="mb-4">
                Fill in what identifies the item, then select <b>Find</b> to see whether it exists.
                <span v-if="usesWildcard">Use <code>*</code> in the name to match any characters.</span>
            </p>

            <div class="identity-fields">
                <div
                    v-for="f in identityFields"
                    :key="f.field"
                    class="field is-horizontal identity-row">
                    <label class="field-label label pad-top">
                        <span>{{ f.label }}:</span>
                    </label>
                    <div class="field-body has-margin-top field is-grouped has-addons">
                        <div class="control is-expanded">
                            <b-select
                                v-if="f.choices.length > 0"
                                v-model="searchTerms[f.field]"
                                expanded>
                                <option value="" />
                                <option
                                    v-for="choice in f.choices"
                                    :key="choice"
                                    :value="choice">
                                    {{ choice }}
                                </option>
                            </b-select>
                            <b-select
                                v-else-if="f.type === 'Bool'"
                                v-model="searchTerms[f.field]"
                                expanded>
                                <option value="" />
                                <option value="true">
                                    True
                                </option>
                                <option value="false">
                                    False
                                </option>
                            </b-select>
                            <code-ref-selector
                                v-else-if="f.type === 'CodeRef'"
                                :selected="searchTerms[f.field]"
                                :code-refs="referableCodes[f.field] ?? []"
                                :show="true"
                                @ref-selected="(identity: string) => searchTerms[f.field] = identity" />
                            <b-input
                                v-else
                                v-model="searchTerms[f.field]"
                                :placeholder="f.field === 'name' ? 'e.g. PUMP or PUMP*' : ''"
                                expanded />
                        </div>
                        <b-tooltip
                            class="control"
                            label="Clear field"
                            position="is-top">
                            <b-button
                                class="control"
                                :disabled="!searchTerms[f.field]"
                                @click="clearField(f.field)">
                                <b-icon
                                    icon="eraser"
                                    size="is-small" />
                            </b-button>
                        </b-tooltip>
                    </div>
                </div>
            </div>

            <p
                v-if="emptySearch"
                class="help is-danger has-text-right">
                Fill in at least one field to search.
            </p>
            <div class="field is-grouped is-grouped-right has-margin-top2">
                <p class="control">
                    <b-button
                        native-type="submit"
                        type="is-primary"
                        :loading="searching">
                        <b-icon
                            icon="magnify"
                            size="is-small" />
                        <span>Find</span>
                    </b-button>
                </p>
            </div>
        </form>

        <hr v-if="(response && outcome) || editor">

        <section v-if="response && outcome && !editor">
            <b-notification
                :type="outcome.messageType"
                :closable="false"
                class="is-light">
                {{ outcome.message }}
                <ul
                    v-if="outcome.reasons.length > 0"
                    class="mt-2">
                    <li
                        v-for="reason in outcome.reasons"
                        :key="reason">
                        {{ reason }}
                    </li>
                </ul>
            </b-notification>

            <b-table
                v-if="rows.length > 0"
                :data="rows"
                :row-class="(hit: CodeSearchHit) => hit.id === outcome?.exactMatch?.id ? 'has-text-weight-bold' : ''"
                striped
                narrowed
                hoverable
                class="block">
                <b-table-column
                    v-slot="{ row }"
                    field="name"
                    label="Name">
                    <a
                        :href="getCodeLinkByB64Id(encodeIdBase64('Code', row.id))"
                        target="_blank">{{ row.name }}</a>
                    <b-tag
                        v-if="row.id === outcome.exactMatch?.id"
                        class="ml-2"
                        type="is-success is-light">
                        exact match
                    </b-tag>
                </b-table-column>
                <b-table-column
                    v-slot="{ row }"
                    field="description"
                    label="Description">
                    {{ row.description }}
                </b-table-column>
                <b-table-column
                    v-slot="{ row }"
                    field="identity"
                    label="Identity">
                    <small>{{ row.identity }}</small>
                </b-table-column>
                <b-table-column
                    v-slot="{ row }"
                    field="isValid"
                    label="Valid"
                    centered>
                    <bool-element :value="row.isValid" />
                </b-table-column>
                <b-table-column
                    v-slot="{ row }"
                    cell-class="has-text-right">
                    <b-button
                        size="is-small"
                        icon-left="pencil"
                        @click="editItem(row)">
                        Edit
                    </b-button>
                </b-table-column>
            </b-table>

            <p
                v-if="response.hasMore"
                class="help mb-3">
                Showing the first {{ response.codes.length }} matches. Refine the search to narrow them down.
            </p>

            <b-button
                v-if="outcome.canCreate"
                type="is-primary"
                icon-left="plus"
                :loading="checkingCreate"
                @click="createItem">
                Create "{{ createLabel }}"
            </b-button>
        </section>

        <section v-if="editor">
            <div class="is-flex is-align-items-center mb-3">
                <h2 class="title is-5 mb-0">
                    <template v-if="editor.mode === 'update'">
                        Update "{{ editor.hit.name }}"
                    </template>
                    <template v-else>
                        Create "{{ editor.label }}"
                    </template>
                </h2>
                <b-button
                    class="ml-auto"
                    icon-left="arrow-left"
                    @click="confirmLeavingEditor(closeEditor)">
                    Back to results
                </b-button>
            </div>
            <governance-information
                v-show="editorLoaded"
                :library-name="library" />
            <code-edit
                v-if="editor.mode === 'update'"
                :key="`update-${editorKey}`"
                :library="library"
                :scopes="scopes"
                :code-id="encodeIdBase64('Code', editor.hit.id)"
                @loaded="editorLoaded = true"
                @refresh="onSaved"
                @cancel="closeEditor" />
            <code-edit
                v-else
                :key="`create-${editorKey}`"
                :library="library"
                :scopes="scopes"
                :code-set-name="codeSetName"
                :code-template-values="editor.template"
                @loaded="editorLoaded = true"
                @refresh="onSaved"
                @cancel="closeEditor" />
        </section>
    </div>
</template>

<style scoped>
.code-find-or-edit {
    position: relative;
    min-height: 8rem;
}

/* Same label alignment as the item editor (CodeEdit.vue). */
.pad-top {
    padding-top: 15px;
}

/* One label column sized to the widest label, so every input starts at the same place. */
.identity-fields {
    display: grid;
    grid-template-columns: max-content minmax(0, 1fr);
    column-gap: 1.5rem;
}

.identity-fields > .identity-row.field.is-horizontal {
    display: contents;
}

.identity-fields .field-label {
    margin-right: 0;
    white-space: nowrap;
    text-align: right;
}
</style>
